/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import supabase from "@/lib/supabase";
import { AuthContextType, UserWithRole } from "./types";
import { AuthContext } from "./context";
import { setAuthState, syncAuthState } from "@/lib/localAuth";
import { authCookies, langCookies } from "@/lib/cookieUtils";
import {
  signInWithAuth,
  signUpWithAuth,
  signOutFromAuth,
  resetPasswordWithAuth,
  fetchUserRole,
} from "./authFunctions";
import { getProfileImage } from "./utils";
import WelcomeModal from "@/components/WelcomeModal";

// مزود سياق المصادقة
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);


  // Handle language preference from user data
  useEffect(() => {
    if (user && user.language_preference) {
      // Apply language preference from user data
      const userLanguage = user.language_preference;
      const currentLanguage = localStorage.getItem("language");

      console.log('User language preference:', userLanguage);

      // Update language if different from current
      if (userLanguage !== currentLanguage) {
        // Set in cookies only
        langCookies.setLanguage(userLanguage as 'ar' | 'en');

        // Set HTML dir attribute for RTL/LTR
        const isRTL = userLanguage === "ar";
        document.documentElement.dir = isRTL ? "rtl" : "ltr";

        // Set HTML lang attribute
        document.documentElement.lang = userLanguage;

        // Add/remove RTL class to body
        if (isRTL) {
          document.body.classList.add("rtl");
          document.body.classList.remove("ltr");
        } else {
          document.body.classList.remove("rtl");
          document.body.classList.add("ltr");
        }

        // Dispatch event to notify components of language change
        setTimeout(() => {
          const event = new CustomEvent("languagechange", {
            detail: { language: userLanguage, isRTL },
          });
          document.dispatchEvent(event);
        }, 50);
      }
    }
  }, [user]);

  useEffect(() => {
    const isMounted = true; // متغير للتحكم في ما إذا كان المكون ما زال متصلاً بالواجهة

    // Clean up auth-related localStorage on first load (one-time migration)
    const cleanupLocalStorage = () => {
      const authKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('sb-') || 
          key.startsWith('user-role-') || 
          key === 'isLoggedIn'
        )) {
          authKeys.push(key);
        }
      }
      authKeys.forEach(key => {
        console.log(`Cleaning up localStorage key: ${key}`);
        localStorage.removeItem(key);
      });
      if (authKeys.length > 0) {
        console.log(`✅ Cleaned up ${authKeys.length} auth-related localStorage items`);
      }
    };
    
    cleanupLocalStorage();

    // Initialize auth state by checking for existing tokens
    syncAuthState();

    // Always sync the isLoggedIn state with the token presence

    // تأكد من عدم استمرار حالة التحميل لفترة طويلة
    const maxLoadingTimeout = setTimeout(() => {
      if (isMounted && isLoading) {
        // Instead of removing auth tokens, we'll keep them and just set isLoading to false
        // This prevents the token disappearing issue while still allowing the app to continue
        setIsLoading(false);
      }
    }, 30000); // زيادة المهلة إلى 30 ثانية بدلاً من 10 ثوانٍ لإعطاء وقت كافٍ للاتصال بالخادم

    // تحقق من حالة تسجيل الدخول المخزنة
    const checkAuthState = async () => {
      try {
        // استرداد الجلسة مباشرة من Supabase بدلاً من الاعتماد على localStorage
        const { data: sessionData } = await supabase.auth.getSession();

        if (!isMounted) return; // التحقق من أن المكون ما زال متصلاً

        if (sessionData.session) {
          setSession(sessionData.session);

          if (sessionData.session.user) {
            console.log(
              "تم العثور على معلومات المستخدم:",
              sessionData.session.user.email
            );

            try {
              // محاولة استرداد الدور المخزن في الكوكيز
              const cachedRole = authCookies.getUserRole(sessionData.session.user.email);

              if (cachedRole) {
                const userWithCachedRole: UserWithRole = {
                  ...sessionData.session.user,
                  role: cachedRole,
                };
                setUser(userWithCachedRole);

                // جلب الدور من قاعدة البيانات في الخلفية لتحديث الذاكرة المخبأة
                fetchUserRole(sessionData.session.user.email).then(
                  (userData) => {
                    if (
                      userData &&
                      userData.role &&
                      userData.role !== cachedRole
                    ) {
                      authCookies.setUserRole(sessionData.session.user.email, userData.role);

                      if (isMounted) {
                        setUser({
                          ...sessionData.session.user,
                          role: userData.role,
                          first_name: userData.first_name,
                          last_name: userData.last_name,
                          profile_image: userData.profile_image,
                          gender: userData.gender,
                          language_preference: userData.language_preference,
                        });
                      }
                    }
                  }
                );
              } else {
                // استخدام Promise.race لتحديد مهلة زمنية لجلب بيانات المستخدم
                const userDataPromise = Promise.race([
                  fetchUserRole(sessionData.session.user.email),
                  new Promise<{
                    role: null;
                    first_name: null;
                    last_name: null;
                    profile_image: null;
                    gender: null;
                    active: null;
                    language_preference?: string;
                  }>((resolve) => {
                    setTimeout(() => {
                      resolve({
                        role: null,
                        first_name: null,
                        last_name: null,
                        profile_image: null,
                        gender: null,
                        active: null,
                        language_preference: "ar",
                      });
                    }, 5000); // زيادة المهلة إلى 5 ثوانٍ
                  }),
                ]);

                const userData = await userDataPromise;

                if (!isMounted) return; // التحقق مرة أخرى بعد العملية غير المتزامنة

                if (userData && userData.role) {
                  // تخزين دور المستخدم في الكوكيز
                  authCookies.setUserRole(sessionData.session.user.email, userData.role);

                  const userWithRole: UserWithRole = {
                    ...sessionData.session.user,
                    role: userData.role,
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    profile_image: userData.profile_image,
                    gender: userData.gender,
                    language_preference: userData.language_preference,
                  };

                  console.log(
                    "تم تحديث بيانات المستخدم في checkAuthState:",
                    userWithRole
                  );
                  setUser(userWithRole);
                } else {
                  console.log(
                    "لم يتم العثور على دور المستخدم أو انتهت المهلة، استخدام دور افتراضي"
                  );
                  // استخدام دور افتراضي "user" إذا لم يتم العثور على دور
                  const userWithDefaultRole: UserWithRole = {
                    ...sessionData.session.user,
                    role: "user",
                  };
                  // تخزين الدور الافتراضي في الكوكيز
                  authCookies.setUserRole(sessionData.session.user.email, "user");
                  setUser(userWithDefaultRole);
                }
              }

              // تحديث حالة تسجيل الدخول في localStorage
              setAuthState(true);
            } catch (roleError) {
              if (!isMounted) return;

              // في حالة الخطأ، نعيّن المستخدم بدون دور
              setUser(sessionData.session.user);
              setAuthState(true);
            }
          } else {
            setUser(null);
            setAuthState(false);
          }
        } else {
          setUser(null);
          setSession(null);
          setAuthState(false);
        }
      } catch (error) {
        if (isMounted) {
          // في حالة حدوث خطأ، نعيّن المستخدم إلى null ونوقف التحميل
          setUser(null);
          setSession(null);
          setAuthState(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuthState();

    // الاستماع لتغييرات حالة المصادقة
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (_event: string, session: Session | null) => {
        setSession(session);

        // عند تغير حالة المصادقة، جلب معلومات الدور من جدول employees
        if (session?.user) {
          try {
            // محاولة استرداد الدور المخزن في الكوكيز
            const cachedRole = authCookies.getUserRole(session.user.email);

            if (cachedRole) {
              const userWithCachedRole: UserWithRole = {
                ...session.user,
                role: cachedRole,
              };
              setUser(userWithCachedRole);

              // جلب الدور من قاعدة البيانات في الخلفية لتحديث الذاكرة المخبأة
              fetchUserRole(session.user.email).then((userData) => {
                if (userData && userData.role && userData.role !== cachedRole) {
                  authCookies.setUserRole(session.user.email, userData.role);
                  setUser({
                    ...session.user,
                    role: userData.role,
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    profile_image: userData.profile_image,
                    gender: userData.gender,
                  });
                }
              });
            } else {
              // جلب الدور والاسم من قاعدة البيانات
              const userData = await fetchUserRole(session.user.email);

              if (userData && userData.role) {
                // تخزين دور المستخدم في الكوكيز
                authCookies.setUserRole(session.user.email, userData.role);

                const userWithRole: UserWithRole = {
                  ...session.user,
                  role: userData.role,
                  first_name: userData.first_name,
                  last_name: userData.last_name,
                  profile_image: userData.profile_image,
                  gender: userData.gender,
                  language_preference: userData.language_preference,
                };

                console.log(
                  "تم تحديث بيانات المستخدم من onAuthStateChange:",
                  userWithRole
                );
                setUser(userWithRole);
              } else {
                // إذا لم يتم العثور على دور، استخدام البيانات الأساسية للمستخدم
                setUser(session.user);
              }
            }
          } catch (err) {
            setUser(session.user);
          }
        } else {
          setUser(null);
        }

        setIsLoading(false);

        // تحديث حالة تسجيل الدخول المخزنة
        setAuthState(!!session);
      }
    );

    // تنظيف الموارد عند إلغاء تركيب المكون
    return () => {
      subscription.unsubscribe();
      clearTimeout(maxLoadingTimeout);
    };
  }, []); // إزالة isLoading و user من التبعيات لتجنب حلقة إعادة التحديث

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const {
        user: authUser,
        session: authSession,
        error,
      } = await signInWithAuth(email, password);

      if (error) {
        return {
          error,
          data: null,
        };
      }

      if (authUser) {
        setUser(authUser);

        // التحقق مما إذا كان هذا هو تسجيل الدخول الأول للمستخدم
        try {
          const { data: userData } = await supabase
            .from("employees")
            .select("last_sign_in_at, language_preference, has_seen_welcome_modal")
            .eq("user_id", authUser.id)
            .single();

          // إضافة سجلات تصحيح لتتبع المشكلة
          console.log('بيانات المستخدم:', userData);
          console.log('قيمة has_seen_welcome_modal:', userData?.has_seen_welcome_modal);

          // Welcome modal is now handled by the component itself using cookies
          
          // تحديث قاعدة البيانات بتاريخ تسجيل الدخول الحالي وعلامة أنه قد شاهد المودال
          await supabase
            .from("employees")
            .update({ 
              last_sign_in_at: new Date().toISOString(),
              // has_seen_welcome_modal field removed - now using cookies
            })
            .eq("user_id", authUser.id);
        } catch (err) {
          // في حالة حدوث خطأ، لا نعرض المودال
          console.error("خطأ في التحقق من تسجيل الدخول الأول:", err);
        }
      }

      if (authSession) {
        setSession(authSession);

        // تحديث حالة تسجيل الدخول في التخزين المحلي
        setAuthState(true);
      }

      return {
        error: null,
        data: authSession,
      };
    } catch (unexpectedError) {
      return {
        error:
          unexpectedError instanceof Error
            ? unexpectedError
            : new Error("حدث خطأ غير متوقع"),
        data: null,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await signUpWithAuth(email, password);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    setIsLoading(true);
    try {
      // أولاً: مسح توكن Supabase من التخزين المحلي

      const { clearAuthData } = await import("@/lib/localAuth");
      await clearAuthData();

      // ثانياً: تحديث التخزين المحلي لإزالة حالة تسجيل الدخول
      setAuthState(false);

      // ثالثاً: تسجيل الخروج من نظام المصادقة
      await signOutFromAuth();

      // رابعاً: إعادة تعيين حالة المستخدم والجلسة
      setUser(null);
      setSession(null);
    } catch (error) {
      // حتى في حالة حدوث خطأ، تأكد من مسح حالة تسجيل الدخول والتوكنات
      try {
        // مسح التوكنات مباشرة
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("sb-")) {
            localStorage.removeItem(key);
          }
        }
        // Fallback to direct localStorage manipulation
        setAuthState(false);
      } catch (clearError) {
        console.error("خطأ في مسح بيانات التخزين المحلي:", clearError);
      }

      setUser(null);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setIsLoading(true);
    try {
      const result = await resetPasswordWithAuth(email);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <WelcomeModal />
    </AuthContext.Provider>
  );
}
